<template>
    <v-dialog
        :model-value="modelValue"
        max-width="560"
        :fullscreen="$vuetify.display.smAndDown"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <v-card>
            <v-card-title>{{ title }}</v-card-title>
            <v-card-text>
                <p v-if="description" class="text-body-2 text-medium-emphasis mb-4">
                    {{ description }}
                </p>
                <v-select
                    v-model="selectedPriceListId"
                    :items="selectPriceLists"
                    :item-title="priceListItemTitle"
                    item-value="id"
                    :label="creatingNew ? 'Creează listă preț' : 'Selectează listă preț'"
                    :variant="fieldVariant"
                    density="compact"
                    hide-details="auto"
                    clearable
                    :dirty="creatingNew"
                >
                    <template #prepend-item>
                        <v-list-item @click="openNewPriceListDialog">
                            Creează listă nouă
                        </v-list-item>
                    </template>
                </v-select>

                <v-alert
                    v-if="creatingNew && !dateApplied"
                    class="mt-4"
                    type="info"
                    variant="tonal"
                    density="compact"
                >
                    Completați data aplicării în dialogul „Creează listă nouă”.
                </v-alert>
            </v-card-text>
            <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn
                    color="danger"
                    variant="elevated"
                    :disabled="loading"
                    @click="close"
                >
                    Anulează
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    style="color: white !important;"
                    :loading="resolvingTarget || loading"
                    :disabled="resolvingTarget || loading"
                    @click="confirm"
                >
                    {{ confirmLabel }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog
            v-model="newListDialog"
            width="500"
            :fullscreen="$vuetify.display.smAndDown"
        >
            <v-card>
                <v-card-title>Creează listă nouă</v-card-title>
                <v-card-text>
                    <v-locale-provider locale="ro">
                        <v-text-field
                            v-model="dateApplied"
                            label="Data aplicării"
                            density="compact"
                            type="date"
                            :variant="fieldVariant"
                            clearable
                            hide-details="auto"
                            @update:model-value="checkExistingPriceList"
                        />
                        <v-text-field
                            v-model="validFromDate"
                            class="mt-4"
                            label="Valabil din data (opțional)"
                            density="compact"
                            type="date"
                            :variant="fieldVariant"
                            clearable
                            hide-details="auto"
                        />
                        <v-alert
                            v-if="showExistingPriceListError"
                            class="mt-4"
                            density="compact"
                            type="warning"
                            title="Atenție!"
                            closable
                            close-label="Închide"
                            text="Pentru data selectată există deja o listă de preț. Lista în lucru va fi aplicată în lista existentă sau selectați altă dată pentru o listă nouă."
                        />
                        <v-alert
                            v-if="copyFromPriceListId"
                            class="mt-4"
                            density="compact"
                            type="info"
                            variant="tonal"
                        >
                            Lista nouă va include produsele din lista curentă; elementele din lista în lucru le vor actualiza sau adăuga.
                        </v-alert>
                    </v-locale-provider>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="danger"
                        variant="elevated"
                        @click="closeNewPriceListDialog"
                    >
                        Anulează
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="elevated"
                        style="color: white !important;"
                        @click="saveNewPriceListDialog"
                    >
                        Salvează
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import { priceListSelectTitle } from '@/utils/utils';
import { createPriceList } from '@/utils/priceListApi';

export default {
    name: 'PriceListTargetDialog',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        priceLists: {
            type: Array,
            default: () => [],
        },
        priceListType: {
            type: String,
            default: 'general',
        },
        defaultPriceListId: {
            type: [Number, String, null],
            default: null,
        },
        copyFromPriceListId: {
            type: [Number, String, null],
            default: null,
        },
        fieldVariant: {
            type: String,
            default: 'outlined',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: 'Selectează lista de preț',
        },
        description: {
            type: String,
            default: '',
        },
        confirmLabel: {
            type: String,
            default: 'Continuă',
        },
    },
    emits: ['update:modelValue', 'confirm'],
    data() {
        return {
            selectedPriceListId: null,
            creatingNew: false,
            newListDialog: false,
            dateApplied: null,
            validFromDate: null,
            showExistingPriceListError: false,
            skipValidFromClearOnNextPriceListId: false,
            resolvingTarget: false,
        };
    },
    computed: {
        selectPriceLists() {
            const lists = [...this.priceLists];
            if (
                this.creatingNew
                && this.dateApplied
                && !this.showExistingPriceListError
                && !lists.some((priceList) => priceList.date_applied === this.dateApplied)
            ) {
                lists.unshift({
                    id: this.pendingPriceListId(this.dateApplied),
                    date_applied: this.dateApplied,
                    valid_from: this.validFromDate,
                });
            }
            return lists;
        },
    },
    watch: {
        modelValue(isOpen) {
            if (isOpen) {
                this.resetState();
            }
        },
        selectedPriceListId(newVal) {
            if (this.skipValidFromClearOnNextPriceListId) {
                this.skipValidFromClearOnNextPriceListId = false;
                return;
            }
            if (newVal && !this.isPendingPriceListId(newVal)) {
                this.validFromDate = null;
                this.creatingNew = false;
            }
        },
    },
    methods: {
        pendingPriceListId(dateApplied) {
            return `new-${dateApplied}`;
        },
        isPendingPriceListId(priceListId) {
            return String(priceListId || '').startsWith('new-');
        },
        priceListItemTitle: priceListSelectTitle,
        todayIsoDate() {
            const today = new Date();
            const pad = (value) => String(value).padStart(2, '0');
            return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
        },
        resetState() {
            this.selectedPriceListId = this.defaultPriceListId || null;
            this.creatingNew = false;
            this.newListDialog = false;
            this.dateApplied = null;
            this.validFromDate = null;
            this.showExistingPriceListError = false;
            this.skipValidFromClearOnNextPriceListId = false;
        },
        close() {
            this.$emit('update:modelValue', false);
        },
        openNewPriceListDialog() {
            this.newListDialog = true;
            this.creatingNew = true;
            this.selectedPriceListId = null;
            this.validFromDate = null;
            if (!this.dateApplied) {
                this.dateApplied = this.todayIsoDate();
            }
        },
        closeNewPriceListDialog() {
            this.newListDialog = false;
            if (!this.selectedPriceListId) {
                this.creatingNew = false;
            }
        },
        saveNewPriceListDialog() {
            if (!this.dateApplied) {
                return;
            }
            this.newListDialog = false;
            if (!this.showExistingPriceListError) {
                this.skipValidFromClearOnNextPriceListId = true;
                this.selectedPriceListId = this.pendingPriceListId(this.dateApplied);
            }
        },
        checkExistingPriceList(dateApplied) {
            const existingList = this.priceLists.find(
                (priceList) => priceList.date_applied === dateApplied,
            );
            if (existingList) {
                this.skipValidFromClearOnNextPriceListId = true;
                this.selectedPriceListId = existingList.id;
                this.showExistingPriceListError = true;
            } else {
                this.selectedPriceListId = null;
                this.showExistingPriceListError = false;
            }
        },
        async resolveTargetPriceListId() {
            if (this.selectedPriceListId && !this.isPendingPriceListId(this.selectedPriceListId)) {
                return this.selectedPriceListId;
            }
            if (!this.creatingNew || !this.dateApplied) {
                return null;
            }

            const createdList = await createPriceList({
                type: this.priceListType,
                dateApplied: this.dateApplied,
                validFrom: this.validFromDate,
                copyFromPriceListId: this.copyFromPriceListId,
            });
            return createdList?.id || null;
        },
        async confirm() {
            this.resolvingTarget = true;
            try {
                const targetPriceListId = await this.resolveTargetPriceListId();
                if (!targetPriceListId) {
                    return;
                }
                this.$emit('confirm', {
                    priceListId: targetPriceListId,
                    createdNewList: this.creatingNew && !this.showExistingPriceListError,
                });
            } finally {
                this.resolvingTarget = false;
            }
        },
    },
};
</script>
